// 2026-09-21 - FormuLearning - 프로젝트 폴더 트리·정렬 순수 로직 (이슈 #77 G6)
// 파일 위치: frontend/src/features/project/folderTree.ts
// 규칙 정본 = 프로세스 설계서 §3.3
//   BR-PRJ-04 정렬 — Name·Created·Modified × 정순·역순. 기본 Modified 역순. Name 은 대소문자 무시·숫자 자연 순서.
//             같은 값이면 Modified 역순. 선택은 브라우저에 보존
//   BR-PRJ-06 이동 — 폴더를 자기 자신·하위로 옮기지 못한다(화면에서도 막는다. 최종 판정은 서버)
//   BR-PRJ-09 배치 — 각 단계에서 하위 폴더(이름 정순 고정) → 프로젝트(선택한 정렬)
// 화면 상태(펼침·정렬)와 무관한 계산만 둔다.

import type { ProjectFolder, ProjectListItem } from '../../types/project'

export type SortKey = 'name' | 'created' | 'modified'
export type SortDir = 'asc' | 'desc'
export interface SortState {
  key: SortKey
  dir: SortDir
}

export const DEFAULT_SORT: SortState = { key: 'modified', dir: 'desc' }
export const SORT_LABELS: Record<SortKey, string> = { name: 'Name', created: 'Created', modified: 'Modified' }

const SORT_STORAGE_KEY = 'formulearn.projectSort'

const collator = new Intl.Collator('ko', { numeric: true, sensitivity: 'base' })

export function loadSort(): SortState {
  try {
    const v = JSON.parse(localStorage.getItem(SORT_STORAGE_KEY) || 'null')
    if (v && v.key in SORT_LABELS && (v.dir === 'asc' || v.dir === 'desc')) return v
  } catch { /* 무시 — 기본값 */ }
  return DEFAULT_SORT
}

export function saveSort(sort: SortState): void {
  try { localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort)) } catch { /* 무시 */ }
}

/** BR-PRJ-04 비교 함수. 동률은 Modified 역순, 그래도 같으면 id 로 고정한다(렌더마다 순서가 흔들리지 않게). */
export function compareProjects(sort: SortState) {
  const sign = sort.dir === 'asc' ? 1 : -1
  return (a: ProjectListItem, b: ProjectListItem): number => {
    const c =
      sort.key === 'name' ? collator.compare(a.name, b.name)
        : sort.key === 'created' ? a.created_at.localeCompare(b.created_at)
          : a.data_updated_at.localeCompare(b.data_updated_at)
    if (c !== 0) return c * sign
    const m = b.data_updated_at.localeCompare(a.data_updated_at)
    return m !== 0 ? m : a.id.localeCompare(b.id)
  }
}

export function compareFolders(a: ProjectFolder, b: ProjectFolder): number {
  return collator.compare(a.name, b.name) || a.id.localeCompare(b.id)
}

export interface FolderIndex {
  byId: Map<string, ProjectFolder>
  /** 상위 id(null = 최상위) → 하위 폴더(이름순) */
  children: Map<string | null, ProjectFolder[]>
  /** 폴더 id(null = 루트) → 직속 프로젝트(정렬 전) */
  projects: Map<string | null, ProjectListItem[]>
}

/**
 * 트리 색인. 목록에 없는 폴더를 가리키는 항목(동시 삭제 직후 등)은 루트로 본다 —
 * 화면에서 사라지는 것보다 루트에 보이는 편이 안전하다.
 */
export function indexTree(folders: ProjectFolder[], projects: ProjectListItem[]): FolderIndex {
  const byId = new Map(folders.map((f) => [f.id, f]))
  const children = new Map<string | null, ProjectFolder[]>()
  for (const f of folders) {
    const parent = f.parent_id && byId.has(f.parent_id) ? f.parent_id : null
    const list = children.get(parent) ?? []
    list.push(f)
    children.set(parent, list)
  }
  for (const list of children.values()) list.sort(compareFolders)
  const byFolder = new Map<string | null, ProjectListItem[]>()
  for (const p of projects) {
    const fid = p.folder_id && byId.has(p.folder_id) ? p.folder_id : null
    const list = byFolder.get(fid) ?? []
    list.push(p)
    byFolder.set(fid, list)
  }
  return { byId, children, projects: byFolder }
}

/** 폴더와 그 모든 하위 폴더 id — 드롭 금지 판정(BR-PRJ-06)용. 자기 자신 포함. */
export function subtreeIds(index: FolderIndex, folderId: string): Set<string> {
  const out = new Set<string>()
  const stack = [folderId]
  while (stack.length) {
    const id = stack.pop()!
    if (out.has(id)) continue
    out.add(id)
    for (const c of index.children.get(id) ?? []) stack.push(c.id)
  }
  return out
}

/** 하위 폴더까지 포함한 프로젝트 수(숨김은 이미 빠진 목록 기준). */
export function countProjects(index: FolderIndex, folderId: string): number {
  let n = 0
  for (const id of subtreeIds(index, folderId)) n += index.projects.get(id)?.length ?? 0
  return n
}

/** 「A / B / C」 경로. 순환 데이터가 들어와도 멈추도록 방문 표시를 둔다. */
export function folderPath(byId: Map<string, ProjectFolder>, folderId: string | null): string {
  const names: string[] = []
  const seen = new Set<string>()
  let cur = folderId
  while (cur && byId.has(cur) && !seen.has(cur)) {
    seen.add(cur)
    const f = byId.get(cur)!
    names.unshift(f.name)
    cur = f.parent_id
  }
  return names.join(' / ')
}

/** 폴더와 그 상위 사슬 id — 현재 프로젝트가 든 폴더를 자동으로 펼칠 때 쓴다. */
export function ancestorIds(byId: Map<string, ProjectFolder>, folderId: string | null): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  let cur = folderId
  while (cur && byId.has(cur) && !seen.has(cur)) {
    seen.add(cur)
    out.push(cur)
    cur = byId.get(cur)!.parent_id
  }
  return out
}

/** «Move to» 목록 — 루트 + 폴더 경로(이름순 깊이 우선). */
export function moveTargets(index: FolderIndex): { id: string | null; label: string; depth: number }[] {
  const out: { id: string | null; label: string; depth: number }[] = [{ id: null, label: 'My Projects (root)', depth: 0 }]
  const walk = (parent: string | null, depth: number) => {
    for (const f of index.children.get(parent) ?? []) {
      out.push({ id: f.id, label: f.name, depth })
      walk(f.id, depth + 1)
    }
  }
  walk(null, 1)
  return out
}
